import * as layout from '../../components/markLayout.module.css';
import { MarkButton, MarkInput, MarkSelect, MarkLabel } from '../../components/MarkControls';
import React, { useId, useMemo, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import useAllSegments from '../../api/useAllSegments';
import useSegment from '../../api/useSegment';
import SegmentAudiencePreview from '../../components/SegmentAudiencePreview';
import type { SegmentSummary, SegmentTargetKind } from '../../types';
type PickedSegment = {
    segment_id: number;
    name: string;
    target_kind: SegmentTargetKind;
};
type Props = {
    value: number | null;
    onChange: (picked: PickedSegment | null) => void;
    disabled?: boolean;
    filterTargetKinds?: SegmentTargetKind[];
};
const SegmentPicker: React.FC<Props> = ({ value, onChange, disabled, filterTargetKinds }) => {
    const selectId = useId();
    const searchId = useId();
    const [search, setSearch] = useState('');
    const { data: segmentsResponse, isLoading, error, refetch, isRefetching } = useAllSegments();
    const segments = segmentsResponse ?? [];
    const { data: detailResponse } = useSegment(value ?? 0);
    const detail = detailResponse?.data;
    const filtered = useMemo(() => {
        let result = segments;
        if (filterTargetKinds && filterTargetKinds.length > 0) {
            const allowed = new Set(filterTargetKinds);
            result = result.filter((s) => allowed.has(s.target_kind));
        }
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            result = result.filter((s) => s.name.toLowerCase().includes(q));
        }
        return result;
    }, [segments, search, filterTargetKinds]);
    const selected =
        segments.find((s) => s.segment_id === value) ??
        (detail && detail.segment_id === value ? detail : null);
    const allowedKinds =
        filterTargetKinds && filterTargetKinds.length > 0 ? new Set(filterTargetKinds) : null;
    const selectedCompatible =
        selected &&
        !filtered.some((segment) => segment.segment_id === selected.segment_id) &&
        (!allowedKinds || allowedKinds.has(selected.target_kind));
    const options = selectedCompatible ? [selected, ...filtered] : filtered;
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.currentTarget.value ? Number(e.currentTarget.value) : null;
        if (!id) {
            onChange(null);
            return;
        }
        const seg = segments.find((s: SegmentSummary) => s.segment_id === id);
        if (seg) {
            onChange({
                segment_id: seg.segment_id,
                name: seg.name,
                target_kind: seg.target_kind
            });
        }
    };
    return (
        <div className={layout.field}>
            <MarkLabel htmlFor={searchId}>Audience segment</MarkLabel>
            <MarkInput
                id={searchId}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder="Search segments by name"
                disabled={disabled}
            />
            <MarkLabel
                htmlFor={selectId}
                style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)'
                }}
                className={layout.srOnly}
            >
                Select segment
            </MarkLabel>
            <MarkSelect
                id={selectId}
                value={value ?? ''}
                onChange={handleChange}
                disabled={disabled || isLoading}
            >
                <option value="">{isLoading ? 'Loading...' : 'Select a segment'}</option>
                {options.map((seg) => (
                    <option key={seg.segment_id} value={seg.segment_id}>
                        {seg.name} ({seg.target_kind})
                    </option>
                ))}
            </MarkSelect>
            {error && (
                <div role="alert" className={layout.notice}>
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-danger)'
                        }}
                    >
                        Failed to load segments.
                    </P>
                </div>
            )}
            {selected && (
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-muted)'
                    }}
                >
                    Selected: {selected.name} ({selected.target_kind}
                    {detail && detail.segment_id === value
                        ? `, ${detail.match_count.toLocaleString()} matches`
                        : ''}
                    )
                </P>
            )}
            {selected && (
                <div>
                    <SegmentAudiencePreview
                        key={selected.segment_id}
                        segmentId={selected.segment_id}
                        segmentName={selected.name}
                        targetKind={selected.target_kind}
                        disabled={disabled}
                    />
                </div>
            )}
            <div className={layout.segmentPickerActions}>
                <MarkButton
                    type="button"
                    variant="outlineSecondary"
                    className={layout.segmentPickerAction}
                    onClick={() =>
                        window.open('/mark/segments/new', '_blank', 'noopener,noreferrer')
                    }
                >
                    Create new segment
                </MarkButton>
                <MarkButton
                    type="button"
                    variant="outlineSecondary"
                    className={layout.segmentPickerAction}
                    onClick={() => refetch()}
                    disabled={isRefetching}
                >
                    {isRefetching ? 'Refreshing…' : 'Refresh list'}
                </MarkButton>
            </div>
        </div>
    );
};
export type { PickedSegment };
export default SegmentPicker;
